import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  Typography,
} from '@mui/material';
import { useResetPassword } from '../hooks/useUserMutations';

const resetPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must contain uppercase letter')
      .regex(/[a-z]/, 'Password must contain lowercase letter')
      .regex(/[0-9]/, 'Password must contain a number'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

interface ResetPasswordDialogProps {
  userId: string | null;
  userEmail: string | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function ResetPasswordDialog({
  userId,
  userEmail,
  open,
  onClose,
  onSuccess,
}: ResetPasswordDialogProps) {
  const resetPassword = useResetPassword();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!userId) return;

    try {
      await resetPassword.mutateAsync({
        id: userId,
        request: { newPassword: data.newPassword },
      });
      handleClose();
      onSuccess();
    } catch (error) {
      // Error handled by mutation
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2}>
            {resetPassword.error && (
              <Alert severity="error">
                {resetPassword.error instanceof Error
                  ? resetPassword.error.message
                  : 'Failed to reset password'}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary">
              Resetting password for: <strong>{userEmail}</strong>
            </Typography>
            <Controller
              name="newPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="New Password"
                  type="password"
                  fullWidth
                  error={!!errors.newPassword}
                  helperText={
                    errors.newPassword?.message ||
                    'Min 8 chars: uppercase, lowercase, number'
                  }
                />
              )}
            />
            <Controller
              name="confirmPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Confirm Password"
                  type="password"
                  fullWidth
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || resetPassword.isPending}
          >
            Reset Password
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
